import { useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { message } from 'antd';
import AuthenticationContext from '../../../contexts/auth/AuthenticationContext';
import CompanyContext from '../../../contexts/company/CompanyContext';
import { getCompanyAiReceptionistCalls } from '../../../../domain/use_cases/ai-receptionist/getCompanyAiReceptionistCalls';
import { markCompanyAiReceptionistCallAsRead } from '../../../../domain/use_cases/ai-receptionist/markCompanyAiReceptionistCallAsRead';
import { AiReceptionistCall } from '../../../../domain/models/AiReceptionist';
import {
  ReceptionistCallsContentFilter,
  ReceptionistCallsPeriod,
  ReceptionistCallsRange,
  ReceptionistDefaultCall,
  buildReceptionistCallDisplay,
  getReceptionistCallsPeriodRange,
  groupReceptionistCallsByDay,
  pinReceptionistDefaultCall,
} from '../types/ReceptionistCallDisplay';

const DEFAULT_PERIOD: ReceptionistCallsPeriod = 'today';
const DEFAULT_CONTENT_FILTER: ReceptionistCallsContentFilter = 'with_content';

const useReceptionistCallsTabController = () => {
  const { authentication } = useContext(AuthenticationContext);
  const { activeCompany, users } = useContext(CompanyContext);

  const [calls, setCalls] = useState<AiReceptionistCall[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [period, setPeriod] = useState<ReceptionistCallsPeriod>(DEFAULT_PERIOD);
  // Dates applied for the custom period, the last 7 days until the user picks others
  const [customRange, setCustomRange] = useState<ReceptionistCallsRange>(() => getReceptionistCallsPeriodRange('last_7_days'));
  const [searchValue, setSearchValue] = useState('');
  const [contentFilter, setContentFilter] = useState<ReceptionistCallsContentFilter>(DEFAULT_CONTENT_FILTER);
  const [urgentOnly, setUrgentOnly] = useState(false);
  const [subscriptionFilter, setSubscriptionFilter] = useState<string[]>([]);
  const [selectedCallId, setSelectedCallId] = useState<string | null>(null);

  // Bumped on every fetch: a page landing after the period or company changed belongs to a stale fetch
  const fetchIdRef = useRef(0);

  const stopFetching = useCallback(() => {
    fetchIdRef.current += 1;
  }, []);

  const companyId = activeCompany?.id;

  // First page shown as soon as it lands, the following ones appended in the background until the period is exhausted
  const fetchCalls = useCallback(async () => {
    if (!companyId || !authentication) return;

    const fetchId = ++fetchIdRef.current;
    const isStale = () => fetchId !== fetchIdRef.current;
    const { start, end } = period === 'custom' ? customRange : getReceptionistCallsPeriodRange(period);

    setIsLoading(true);
    setIsLoadingMore(false);
    try {
      let response = await getCompanyAiReceptionistCalls({ company_id: companyId, start, end }, authentication);
      if (isStale()) return;
      setCalls(response.calls);
      setIsLoading(false);

      // Each receptionist pages on its own, so a page can hold calls more recent than the previous one: re-sorted
      while (response.next_cursors !== null) {
        setIsLoadingMore(true);
        response = await getCompanyAiReceptionistCalls(
          { company_id: companyId, start, end, cursors: response.next_cursors },
          authentication,
        );
        if (isStale()) return;
        const page = response.calls;
        setCalls((previous) => [...previous, ...page].sort((a, b) => b.created - a.created));
      }
    } catch (e) {
      if (isStale()) return;
      const errorMessage = e instanceof Error ? e.message : 'Erreur lors du chargement des appels';
      message.error(errorMessage);
    } finally {
      if (!isStale()) {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    }
  }, [companyId, authentication, period, customRange]);

  useEffect(() => {
    // Synchro avec une source distante : le setState synchrone est le passage en chargement qui precede l'appel.
    // eslint-disable-next-line react/set-state-in-effect
    fetchCalls();
    // Stops the background paging when the tab unmounts
    return stopFetching;
  }, [fetchCalls, stopFetching]);

  const userNameBySubscription = useMemo(() => {
    const names = new Map<string, string>();
    users.forEach((user) => {
      const userName = `${user.firstname} ${user.lastname}`.trim() || user.email || '';
      Object.keys(user.idSubscription || {}).forEach((subscriptionId) => names.set(subscriptionId, userName));
    });
    return names;
  }, [users]);

  const displayedCalls = useMemo(
    () => calls.map((call) => buildReceptionistCallDisplay(call, userNameBySubscription.get(call.subscription_id) || '')),
    [calls, userNameBySubscription],
  );

  const userOptions = useMemo(() => {
    const subscriptionIds = [...new Set(displayedCalls.map((call) => call.subscriptionId))];
    return subscriptionIds.map((subscriptionId) => ({
      value: subscriptionId,
      label: userNameBySubscription.get(subscriptionId) || 'Non assigné',
    }));
  }, [displayedCalls, userNameBySubscription]);

  const totals = useMemo(() => {
    const withContentCount = displayedCalls.filter((call) => call.hasContent).length;
    return {
      callsCount: displayedCalls.length,
      withContentCount,
      withoutContentCount: displayedCalls.length - withContentCount,
      durationMs: displayedCalls.reduce((total, call) => total + call.durationMs, 0),
      opportunitiesCount: displayedCalls.filter((call) => call.isOpportunity).length,
    };
  }, [displayedCalls]);

  const filteredCalls = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    return displayedCalls.filter((call) => {
      if (contentFilter === 'with_content' && !call.hasContent) return false;
      if (contentFilter === 'without_content' && call.hasContent) return false;
      if (urgentOnly && !call.isUrgent) return false;
      if (subscriptionFilter.length > 0 && !subscriptionFilter.includes(call.subscriptionId)) return false;
      if (!query) return true;
      return [call.callerName, call.callerPhone, call.title ?? '', call.summaryMarkdown].some((value) =>
        value.toLowerCase().includes(query),
      );
    });
  }, [displayedCalls, searchValue, contentFilter, urgentOnly, subscriptionFilter]);

  const hasActiveFilters =
    period !== DEFAULT_PERIOD ||
    searchValue !== '' ||
    contentFilter !== DEFAULT_CONTENT_FILTER ||
    urgentOnly ||
    subscriptionFilter.length > 0;

  const resetFilters = () => {
    setPeriod(DEFAULT_PERIOD);
    setSearchValue('');
    setContentFilter(DEFAULT_CONTENT_FILTER);
    setUrgentOnly(false);
    setSubscriptionFilter([]);
  };

  const groups = useMemo(() => groupReceptionistCallsByDay(filteredCalls), [filteredCalls]);

  const periodKey = period === 'custom' ? `${customRange.start}-${customRange.end}` : period;
  const filtersKey = [periodKey, searchValue, contentFilter, urgentOnly, subscriptionFilter.join(',')].join('|');
  const [defaultCall, setDefaultCall] = useState<ReceptionistDefaultCall>({ filtersKey, id: null });
  const pinnedDefaultCall = pinReceptionistDefaultCall(defaultCall, filteredCalls, filtersKey);
  if (pinnedDefaultCall !== defaultCall) {
    setDefaultCall(pinnedDefaultCall);
  }

  const selectedCall =
    filteredCalls.find((call) => call.id === selectedCallId) ??
    filteredCalls.find((call) => call.id === pinnedDefaultCall.id) ??
    filteredCalls[0] ??
    null;

  // Only the user's own unread calls count: a supervisor cannot clear a member's unread
  const unreadCount = displayedCalls.filter((call) => call.isUnread && call.isOwn).length;

  // Only an explicit click marks read, not the first call shown by default on load
  const selectedCallIdToMark = selectedCall?.id === selectedCallId && selectedCall.isUnread && selectedCall.isOwn ? selectedCall.id : null;

  // Same flag as the mobile (readed_frontend): the displayed call is read, on web and mobile alike
  useEffect(() => {
    if (!selectedCallIdToMark || !companyId || !authentication) return;

    markCompanyAiReceptionistCallAsRead({ company_id: companyId, call_id: selectedCallIdToMark }, authentication)
      .then(() => setCalls((previous) => previous.map((call) => (call.id === selectedCallIdToMark ? { ...call, readed_frontend: true } : call))))
      .catch((e) => console.error('markCompanyAiReceptionistCallAsRead error:', e));
  }, [selectedCallIdToMark, companyId, authentication]);

  return {
    state: {
      groups,
      callsCount: filteredCalls.length,
      unreadCount,
      selectedCall,
      totals,
      isLoading,
      isLoadingMore,
      period,
      customRange,
      searchValue,
      contentFilter,
      urgentOnly,
      subscriptionFilter,
      userOptions,
      hasActiveFilters,
      canFilterByUser: !!activeCompany?.isSupervisor,
    },
    actions: {
      setPeriod,
      setCustomRange,
      setSearchValue,
      setContentFilter,
      setUrgentOnly,
      setSubscriptionFilter,
      resetFilters,
      selectCall: setSelectedCallId,
      refresh: fetchCalls,
    },
  };
};

export default useReceptionistCallsTabController;
